import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ArrowLeft } from 'lucide-react';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile, loading } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user && profile) {
      // Redirect RH users to RH dashboard, others to main dashboard
      if (profile.role === 'rh') {
        navigate('/dashboard?tab=rh');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, profile, navigate]);

  // Função para limpar estado de autenticação
  const cleanupAuthState = () => {
    console.log('🔐 LoginPage: Limpando estado de autenticação...');
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      console.log('🔐 LoginPage: Tentativa de login para:', formData.email);
      
      // Limpar estado antes do login
      cleanupAuthState();
      
      // Tentar fazer logout global primeiro
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        console.log('🔐 LoginPage: Logout prévio falhou (esperado)');
      }

      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      console.log('🔐 LoginPage: Resultado do login:', { data, authError });

      if (authError) {
        throw authError;
      }

      if (data.user) {
        console.log('🔐 LoginPage: Login bem-sucedido para:', data.user.email);
        // Don't redirect immediately, let useEffect handle it based on profile
        // The AuthContext will load the profile and trigger the redirect
      } else {
        setError('Email ou senha incorretos');
      }
    } catch (err: any) {
      console.error('🔐 LoginPage: Erro no login:', err);
      if (err.message?.includes('Invalid login credentials')) {
        setError('Email ou senha incorretos');
      } else {
        setError('Erro ao fazer login. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };


  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Background Image with Overlay */}
      <div 
        className="flex-1 lg:flex-[2] relative bg-cover bg-center bg-no-repeat min-h-[40vh] lg:min-h-screen"
        style={{
          backgroundImage: `url('/lovable-uploads/86bced1c-eb30-408c-b4a2-c762d9427868.png')`
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col h-full p-4 sm:p-6 lg:p-12">
          {/* Logo */}
          <div className="mb-4 lg:mb-auto">
            <img 
              src="/lovable-uploads/b45dae5a-5dfa-45a0-bc80-52e7a950e93e.png" 
              alt="Fox Vendas"
              className="h-12 lg:h-16 w-auto"
            />
          </div>
          
          {/* Welcome Content */}
          <div className="flex-1 flex flex-col justify-center max-w-md">
            <h1 className="text-white text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold font-inter mb-4 lg:mb-6 leading-tight">
              Seja<br />Bem Vindo
            </h1>
            <p className="text-white text-base sm:text-lg lg:text-xl font-inter opacity-90 leading-relaxed">
              O mercado não espera. Sua performance começa com um clique. Conecte-se e acelere suas vendas.
            </p>
          </div>

          {/* Back to Site Link */}
          <div className="mt-4 lg:mt-auto">
            <Link 
              to="/"
              className="inline-flex items-center text-white hover:text-orange-300 text-sm font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao site
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 bg-white flex items-center justify-center p-4 sm:p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 font-inter mb-2">
              Acesse o seu<br />Dashboard
            </h2>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-orange-500" />
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full pl-10 pr-4 py-4 bg-white border border-orange-500 rounded-lg hover:border-orange-600 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors font-inter"
                  placeholder="Endereço de e-mail"
                />
              </div>
            </div>

            <div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-orange-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full pl-10 pr-12 py-4 bg-white border border-orange-500 rounded-lg hover:border-orange-600 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors font-inter"
                  placeholder="Senha"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="text-right">
              <button
                type="button"
                onClick={async () => {
                  const email = prompt('Digite seu email para redefinir a senha:');
                  if (email) {
                    
                    setIsLoading(true);
                    setError('');

                    try {
                      // Gerar link de redefinição
                      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
                        redirectTo: `${window.location.origin}/reset-password`,
                      });

                      if (resetError) {
                        throw resetError;
                      }

                      // Criar link personalizado para o email
                      const resetLink = `${window.location.origin}/reset-password?email=${encodeURIComponent(email)}`;

                      // Enviar email customizado via edge function
                      const response = await fetch(`https://ngagtienwooguwquvtbn.supabase.co/functions/v1/send-password-reset`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          email: email,
                          resetLink: resetLink
                        })
                      });

                      if (!response.ok) {
                        throw new Error('Erro ao enviar email');
                      }

                      setResetEmailSent(true);
                      setError('');
                    } catch (err: any) {
                      console.error('Erro ao enviar email de redefinição:', err);
                      setError('Erro ao enviar email de redefinição. Tente novamente.');
                    } finally {
                      setIsLoading(false);
                    }
                  }
                }}
                className="text-sm text-gray-600 hover:text-gray-800 font-inter"
              >
                Esqueci minha senha
              </button>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-orange-500 text-white py-4 rounded-lg hover:bg-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed font-inter flex items-center justify-center"
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
              {!isLoading && <ArrowLeft className="w-5 h-5 ml-2 rotate-180" />}
            </button>
          </form>

          {/* Success Message for Password Reset */}
          {resetEmailSent && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
              <div className="w-5 h-5 text-green-500 flex-shrink-0">✓</div>
              <div>
                <p className="text-green-700 text-sm font-medium">Email enviado com sucesso!</p>
                <p className="text-green-600 text-xs mt-1">
                  Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
                </p>
              </div>
            </div>
          )}


          {/* Fox Logo at Bottom */}
          <div className="mt-12 flex justify-center">
            <img 
              src="/lovable-uploads/c4ed7c15-9bc2-474b-8028-79791b79f367.png" 
              alt="Fox Vendas"
              className="h-12 w-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;